import { useCallback, useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { messageLocaleOf, readRegistrationStatus, register, requestEmailVerification } from "../../api/accountsApi";
import ErrorNotice from "../../errors/ErrorNotice";
import { useLoad } from "../../shared/loading/useLoad";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import StatusMessage from "../../ui/feedback/StatusMessage";
import { FORM_CONTROL_CLASS } from "../../ui/styles/formStyles";
import AuthCardPage from "./AuthCardPage";
import BackToSignInLink from "./BackToSignInLink";
import NewPasswordFields from "./NewPasswordFields";
import Field from "../../ui/forms/Field";
import LoadStatus from "../../ui/feedback/LoadStatus";

/**
 * `/register`: create an account (Q-36, as in v1). The server mails a link that confirms the
 * address; with `with_approval` an administrator decides next. The answer never says whether
 * the email had an account already.
 */
export default function RegisterPage() {
  const { t } = useTranslation();
  const loadStatus = useCallback((signal: AbortSignal) => readRegistrationStatus(signal), []);
  const { state: status, reload } = useLoad(loadStatus);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  return (
    <AuthCardPage title={t("register.title")}>
      <LoadStatus state={status} loadingLabel={t("common.loading")} onRetry={reload} />
      {status.status === "loaded" && status.value.selfRegistration === "off" ? (
        <StatusMessage tone="info">{t("register.closed")}</StatusMessage>
      ) : null}
      {status.status === "loaded" && status.value.selfRegistration !== "off" && submittedEmail === null ? (
        <RegistrationForm onRegistered={setSubmittedEmail} />
      ) : null}
      {status.status === "loaded" && submittedEmail !== null ? (
        <CheckYourEmail email={submittedEmail} emailDelivery={status.value.emailDelivery} />
      ) : null}
      <BackToSignInLink />
    </AuthCardPage>
  );
}

function RegistrationForm({ onRegistered }: { onRegistered: (email: string) => void }) {
  const { t, i18n } = useTranslation();
  const { state, submit } = useSubmission();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [mismatch, setMismatch] = useState(false);

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirmation) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    submit(async () => {
      await register({ email, displayName, password, locale: messageLocaleOf(i18n.language) });
      onRegistered(email);
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <p className="app-text-muted mb-4 text-sm">{t("register.intro")}</p>
      <fieldset disabled={state.status === "submitting"}>
        <Field id="register-email" label={t("newAccount.email")}>
          {(control) => (
            <input
              {...control}
              type="email"
              className={`mb-3 ${FORM_CONTROL_CLASS}`}
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              autoComplete="email"
              required
            />
          )}
        </Field>
        <Field id="register-name" label={t("newAccount.displayName")}>
          {(control) => (
            <input
              {...control}
              className={`mb-3 ${FORM_CONTROL_CLASS}`}
              value={displayName}
              onChange={(event) => {
                setDisplayName(event.target.value);
              }}
              autoComplete="name"
              required
            />
          )}
        </Field>
        <NewPasswordFields
          idPrefix="register"
          password={password}
          confirmation={confirmation}
          onPasswordChange={setPassword}
          onConfirmationChange={setConfirmation}
        />
        {mismatch ? <StatusMessage tone="error" className="mb-3">{t("newAccount.passwordMismatch")}</StatusMessage> : null}
        {state.status === "failed" ? <ErrorNotice error={state.error} className="mb-3" /> : null}
        <AppButton type="submit" className="w-full" variant="primary">
          {state.status === "submitting" ? t("login.pleaseWait") : t("register.submit")}
        </AppButton>
      </fieldset>
    </form>
  );
}

/** After registering: where the link went, and how to get another one. */
function CheckYourEmail({ email, emailDelivery }: { email: string; emailDelivery: boolean }) {
  const { t } = useTranslation();
  const { state, submit } = useSubmission();

  return (
    <div className="space-y-3">
      <StatusMessage tone="success" title={t("register.checkEmailTitle")}>
        <p>{t("register.checkEmailMessage", { email })}</p>
      </StatusMessage>
      {emailDelivery ? null : <StatusMessage tone="info">{t("register.noEmailDelivery")}</StatusMessage>}
      {emailDelivery ? (
        <AppButton
          type="button"
          size="sm"
          disabled={state.status === "submitting"}
          onClick={() => {
            submit(() => requestEmailVerification(email));
          }}
        >
          {t("register.resend")}
        </AppButton>
      ) : null}
      {state.status === "succeeded" ? <StatusMessage tone="success">{t("register.resent")}</StatusMessage> : null}
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
    </div>
  );
}
